import { Form, Formik } from 'formik'
import { observer } from 'mobx-react-lite'
import React, { useContext, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { CurrentProjectManagerContext } from '../../../../../contexts/CurrentProjectManagerContext'
import { InfluencerContext } from '../../../../../contexts/InfluencerContext'
import axiosService from '../../../../../utils/AxiosService'
import Textarea from '../../../../FormComponents/Textarea'
import { toastError, toastSuccess } from '../../../../FormComponents/Toasts'
import Modal from '../../../../Modal/Modal'
import RatingModel from '../../../../../models/Rating'
import { RatingSchema } from '../../../../../schemas/RatingSchema'
import StarPicker from './StarPicker'

const SECTIONS = [
  { score: 'professional_score', description: 'professional_description', title: 'Professional work', hint: 'Communication, reliability, meeting deadlines and briefings' },
  { score: 'quality_score', description: 'quality_description', title: 'Quality of content', hint: 'Creativity, production quality and audience fit' },
]

export default observer(function RatingModal({ setIsOpen, isOpen }) {
  const { id } = useParams()
  const { ratings } = useContext(InfluencerContext)
  const { currentProjectManager } = useContext(CurrentProjectManagerContext)

  const existingRating = useMemo(() => ratings.find(rating => +rating.project_manager_id === +currentProjectManager.id), [ratings.length])
  const initialRating = useMemo(() => new RatingModel(existingRating), [existingRating])

  const handleSubmit = (rating, { setSubmitting }) => {
    const request = existingRating
      ? axiosService.patch(`/api/project_manager/authentications/${id}/ratings/${existingRating.id}`, { rating })
      : axiosService.post(`/api/project_manager/authentications/${id}/ratings`, { rating })

    request
      .then(savedRating => {
        if (existingRating) {
          Object.assign(existingRating, savedRating)
          toastSuccess('Review updated')
        } else {
          ratings.unshift(savedRating)
          toastSuccess('Review posted')
        }
        setIsOpen(false)
      })
      .catch(response => {
        toastError(response?.data?.error || 'Could not save the review')
        setSubmitting(false)
      })
  }

  return (
    <Modal isOpen={isOpen} onClose={() => setIsOpen(false)}>
      <Modal.Header>{existingRating ? 'Edit your review' : 'Write a review'}</Modal.Header>
      <Formik
        initialValues={{
          professional_score: initialRating.professional_score,
          professional_description: initialRating.professional_description,
          quality_score: initialRating.quality_score,
          quality_description: initialRating.quality_description,
        }}
        onSubmit={handleSubmit}
        validationSchema={RatingSchema}
      >
        {({ handleBlur, handleChange, values, setFieldValue, errors, submitCount, isSubmitting }) => (
          <Form>
            <Modal.Body>
              <div className="w-half-screen flex flex-col gap-4">
                {SECTIONS.map(section => (
                  <div key={section.score} className="rounded-xl border-1 border-gray-100 dark:border-rb-dark-mode-lighter p-4">
                    <p className="font-bold text-lg dark:text-white">{section.title}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">{section.hint}</p>
                    <StarPicker
                      value={values[section.score]}
                      onChange={star => setFieldValue(section.score, star, true)}
                      error={submitCount > 0 ? errors[section.score] : null}
                    />
                    <Textarea
                      rows={4}
                      topMargin="mt-3"
                      placeholder="Share some details (optional)"
                      name={section.description}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values[section.description]}
                    />
                  </div>
                ))}
              </div>
            </Modal.Body>
            <Modal.Footer>
              <div className="w-full flex justify-between items-center">
                <Modal.DissmissButton className="rb-btn-cancel px-4 py-2 text-sm font-semibold rounded-lg">
                  Cancel
                </Modal.DissmissButton>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 text-sm font-semibold text-white bg-rb-green rounded-lg dark:text-rb-dark-mode disabled:opacity-50"
                >
                  {existingRating ? 'Save changes' : 'Post review'}
                </button>
              </div>
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </Modal>
  )
})
